import { useTranslation } from 'react-i18next';

import { Button } from './Button';
import { Modal } from './Modal';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  /**
   * Styl przycisku potwierdzenia + default label:
   *  - `danger` (default) — czerwony, label „Usuń". Dla akcji destrukcyjnych
   *    (usuwanie, suspend, anonimizacja).
   *  - `primary` — zielony akcent, label „Potwierdź". Dla akcji nie-destrukcyjnych
   *    (wyślij link, aktywuj, wyloguj sesje, przywróć).
   */
  variant?: 'danger' | 'primary';
  /**
   * Napis przycisku zamykającego okno bez akcji. Domyślnie „Anuluj" (`common.cancel`) — zmieniać
   * tylko w oknie, w którym akcja (albo inny wariant tego samego okna) też zaczyna się od
   * „Anuluj…" (np. „Anuluj proces"), żeby dwa przyciski obok siebie nie brzmiały tak samo.
   */
  cancelLabel?: string;
  /**
   * Akcja potwierdzenia w toku (mutacja) — pokazuje spinner na przycisku i blokuje
   * oba przyciski, dopóki caller nie zamknie dialogu (zwykle w onSuccess). Opcjonalne,
   * wstecznie zgodne: brak = dotychczasowe zachowanie (bez spinnera).
   */
  confirming?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  variant = 'danger',
  cancelLabel,
  confirming = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const defaultLabel = variant === 'danger' ? t('common.delete') : t('common.confirm');
  const label = confirmLabel ?? defaultLabel;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={message}
      size="sm"
      role="alertdialog"
      // W trakcie potwierdzania (mutacja) tło i Esc nie anulują okna — jak `!submitting` w innych
      // oknach. X w nagłówku zostaje (jawna akcja).
      closeOnBackdrop={!confirming}
      closeOnEsc={!confirming}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={confirming}
            data-testid="confirm-dialog-cancel-btn"
          >
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={confirming}
            data-testid="confirm-dialog-confirm-btn"
          >
            {label}
          </Button>
        </>
      }
    />
  );
}
